// Markt-Dashboard in Merxo, Abschnitt 3b (docs/integration/bestell-app.md §20, Stufe 3 der Verschmelzung):
//   S5  Anliegen zwischen App und Merxo – Kanal: `sales.channel.submitCase | cases | reportCase` (+ Ereignis `caseChanged`);
//       Oberfläche: `sales.case.list | get | decide | request | customer | count` (Rechte `sales.case.read | write`)
//   S6  Abos der Bestell-App – Kanal: `sales.channel.reportSubscriptions` (vollständige Liste); Oberfläche: `sales.appSubscription.list`
//   S8  Einwilligungen – Kanal: `sales.channel.reportConsents` (nur der letzte Stand); Anzeige im Kasten „Bestell-App“ am Kunden
// Die Formen von `cases`, `reportCase`, `reportSubscriptions` und `reportConsents` richten sich nach der Bestell-App (docs/MERXO.md 13.8–13.10).
import { defineContract, defineEvent } from '@shared/contract'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { APP_CASE_KINDS, CASE_KINDS, CASE_ORIGINS, CASE_STATUSES, CHANNEL_APP_CASE_KINDS, MERXO_CASE_KINDS, SUBSCRIPTION_RHYTHMS } from '../lib/cases'
import { zDay } from './common'
import { brand } from '@shared/brand'

const zText = (max: number) => z.string().trim().max(max)
const zKind = z.enum(CASE_KINDS)
const zStatus = z.enum(CASE_STATUSES)
const zOrigin = z.enum(CASE_ORIGINS)
// Kanal-Verträge der Bestell-App: Arten und Herkunft wie vor dem Telefonassistenten (Interessenten sieht die App nie, TA-R11)
const zAppKind = z.enum(CHANNEL_APP_CASE_KINDS)
const zAppOrigin = z.enum(['app', 'merxo'])

// ---- Kanal: Anliegen (S5) ---------------------------------------------------------------------------------------------------------

export const channelSubmitCase = defineContract({
  name: 'sales.channel.submitCase',
  title: 'Anliegen eines Vertriebskanals einreichen',
  description:
    `Reicht einen Geschäftskunden-Antrag oder einen Löschantrag der Bestell-App ein (idempotent über \`caseId\`); das Personal entscheidet in ${brand.name}.`,
  llm: {
    description:
      'Schnittstelle der Bestell-App (öffentliche API). Reicht ein Anliegen der Kundschaft ein: `business_account` (Firma, Ansprechperson, E-Mail, ' +
      'Anschrift, USt-IdNr., Segment, Nachricht) oder `account_deletion` (E-Mail, Kundennummer, Anmerkung). Alle Angaben sind Text; nur diese Angaben ' +
      'werden angenommen. Dieselbe `caseId` legt kein zweites Anliegen an. Es entsteht eine Aufgabe für das Personal.',
    keywords: ['Anliegen', 'Geschäftskunden-Antrag', 'Löschantrag', 'Bestell-App', 'Schnittstelle'],
  },
  input: z.strictObject({
    caseId: zText(64).min(1, { error: 'Die Kennung des Anliegens fehlt.' }),
    kind: z.enum(APP_CASE_KINDS, { error: 'Aus der App gibt es nur business_account und account_deletion.' }),
    payload: z.record(z.string(), z.string({ error: 'Alle Angaben zum Anliegen sind Text.' }), {
      error: 'Die Angaben zum Anliegen fehlen.',
    }),
  }),
  output: z.object({ caseId: z.string(), status: zStatus, replayed: z.boolean() }),
  permission: 'sales.order.write',
  risk: 'write',
  scope: 'record',
  publicApi: true,
  assistant: 'never',
})

const zChannelCase = z.object({
  caseId: z.string(),
  origin: zAppOrigin,
  kind: zAppKind,
  status: zStatus,
  /** Cursor: fortlaufend je Mandant, jede Änderung von Zustand oder Entscheidung vergibt die nächste Nummer */
  seq: z.int(),
  payload: z.record(z.string(), z.unknown()),
  /** Entscheidung in Merxo: `customerNumber` bei angenommenem Geschäftskunden-Antrag, `reason` bei Ablehnung */
  decision: z.object({ customerNumber: z.string().nullable(), reason: z.string().nullable() }).nullable(),
})

export const channelCases = defineContract({
  name: 'sales.channel.cases',
  title: 'Anliegen für einen Vertriebskanal abrufen',
  description:
    `Entscheidungen zu Anträgen der Bestell-App und Aufträge aus ${brand.name} (Konto zuordnen, Widerruf eintragen, Abo pausieren/fortsetzen), mit Cursor.`,
  llm: {
    description:
      'Schnittstelle der Bestell-App (öffentliche API). Liefert höchstens 100 Anliegen nach dem Cursor `after`, aufsteigend nach `seq`, und den neuen Cursor (undurchsichtige Zeichenkette `<Epoche>:<seq>`; fremd oder unlesbar = von vorn). ' +
      `Anträge der App erscheinen mit ihrer Entscheidung (\`accepted\` mit \`decision.customerNumber\`, \`rejected\` mit \`decision.reason\`), Aufträge aus ${brand.name} ` +
      '(`origin: merxo`) im Zustand `open`. Die App führt aus und meldet das Ergebnis mit sales.channel.reportCase.',
    keywords: ['Anliegen', 'Entscheidung', 'Cursor', 'Bestell-App', 'Schnittstelle'],
  },
  input: z.strictObject({ after: zText(80).optional() }),
  output: z.object({ cases: z.array(zChannelCase), cursor: z.string() }),
  permission: 'sales.case.read',
  risk: 'read',
  scope: 'any',
  publicApi: true,
  assistant: 'never',
})

export const channelReportCase = defineContract({
  name: 'sales.channel.reportCase',
  title: 'Ergebnis eines Anliegens von einem Vertriebskanal entgegennehmen',
  description:
    'Die Bestell-App meldet, ob sie eine Entscheidung bzw. einen Auftrag ausgeführt hat (`done`) oder nicht (`failed`). Idempotent.',
  llm: {
    description:
      'Schnittstelle der Bestell-App (öffentliche API). Meldet das Ergebnis eines Anliegens: `done` oder `failed`, optional mit `result` ' +
      '(z. B. customerNumber, revoked, active) und einem Satz `message`. Ein Anliegen der App muss vorher entschieden sein. Wiederholungen ' +
      'ändern nichts (`replayed: true`).',
    keywords: ['Anliegen', 'Ergebnis', 'Bestell-App', 'Schnittstelle'],
  },
  input: z.strictObject({
    caseId: zText(64).min(1, { error: 'Die Kennung des Anliegens fehlt.' }),
    outcome: z.enum(['done', 'failed'], { error: 'Bitte done oder failed angeben.' }),
    result: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).optional(),
    message: zText(500).optional(),
  }),
  output: z.object({ caseId: z.string(), status: zStatus, replayed: z.boolean() }),
  permission: 'sales.order.write',
  risk: 'write',
  scope: 'record',
  publicApi: true,
  assistant: 'never',
})

/** Webhook „Bestell-App“ (sales.channel.*): nur Auslöser – die App holt die Anliegen mit sales.channel.cases. Entprellt auf 2 s. */
export const channelCaseChanged = defineEvent({
  name: 'sales.channel.caseChanged',
  title: 'Anliegen für einen Vertriebskanal entschieden oder erteilt',
  payload: z.object({ seq: z.int() }),
})

// ---- Kanal: Abos und Einwilligungen (S6, S8) -----------------------------------------------------------------------------------------

/** Tolerant gegenüber neuen Feldern der App (sie werden ignoriert); leere Termine und Fenster gelten als fehlend. */
const zEmptyToUndefined = <T extends z.ZodType>(schema: T) => z.preprocess((value) => (value === '' ? undefined : value), schema.optional())

const zSubscriptionSnapshot = z.object({
  subscriptionId: zText(80).min(1, { error: 'Die Kennung eines Abos fehlt.' }),
  accountEmail: zText(200),
  customerNumber: zEmptyToUndefined(zText(40)),
  name: zText(200),
  rhythm: z.enum(SUBSCRIPTION_RHYTHMS),
  weekday: z.int().min(0).max(7),
  /** Beginn des Lieferfensters, z. B. `09:00` */
  window: zEmptyToUndefined(zText(20)),
  fulfillment: z.literal('delivery'),
  lines: z.array(z.object({ sku: zText(60), qty: z.int().min(0).max(1_000_000) })).max(200),
  active: z.boolean(),
  nextDate: z.preprocess((value) => (value === '' ? null : value), zDay.nullish()),
  lastRun: z
    .object({
      date: zDay,
      result: z.enum(['created', 'failed']),
      at: z.string().max(40),
      orderNumber: zEmptyToUndefined(zText(60)),
      message: zEmptyToUndefined(zText(500)),
    })
    .nullish(),
})

export const channelReportSubscriptions = defineContract({
  name: 'sales.channel.reportSubscriptions',
  title: 'Abos des Vertriebskanals melden',
  description:
    `Die Bestell-App meldet die vollständige Liste ihrer Abos; ${brand.name} zeigt sie an. Fehlende Abos gelten als gelöscht.`,
  llm: {
    description:
      'Schnittstelle der Bestell-App (öffentliche API). Ersetzt den Lesestand der Abos durch die gemeldete, VOLLSTÄNDIGE Liste (`complete: true` ' +
      `ist Pflicht): Abos, die fehlen, gelten als gelöscht. Die App führt die Abos selbst aus; ${brand.name} zeigt sie nur an. Höchstens 500.`,
    keywords: ['Abo', 'Abonnement', 'Bestell-App', 'Schnittstelle', 'Lesestand'],
  },
  input: z.strictObject({
    subscriptions: z.array(zSubscriptionSnapshot).max(500, { error: 'Bitte höchstens 500 Abos melden.' }),
    complete: z.literal(true, { error: 'Bitte die vollständige Liste melden (complete: true).' }),
  }),
  output: z.object({ count: z.int(), removed: z.int() }),
  permission: 'sales.order.write',
  risk: 'write',
  scope: 'record',
  publicApi: true,
  assistant: 'never',
  limits: { maxItems: 500, itemCount: (input) => input.subscriptions.length },
})

export const channelReportConsents = defineContract({
  name: 'sales.channel.reportConsents',
  title: 'Einwilligungen verknüpfter Kunden melden',
  description:
    'Die Bestell-App meldet den aktuellen Stand der Einwilligungen verknüpfter Kunden (Werbe-Mitteilungen, Newsletter) – ohne Verlauf.',
  llm: {
    description:
      'Schnittstelle der Bestell-App (öffentliche API). Meldet je verknüpftem Kunden den aktuellen Stand: Werbe-Mitteilungen (`offersPush`: ' +
      `eingewilligt oder widerrufen seit …, null = nie eingewilligt) und Newsletter. ${brand.name} speichert nur den letzten Stand, keinen Verlauf; den ` +
      'Nachweis führt die App. Unbekannte Kundennummern werden genannt und übergangen. Höchstens 500.',
    keywords: ['Einwilligung', 'Werbe-Mitteilungen', 'Newsletter', 'Bestell-App', 'Schnittstelle'],
  },
  input: z.strictObject({
    consents: z
      .array(
        z.strictObject({
          customerNumber: zText(40).min(1, { error: 'Die Kundennummer fehlt.' }),
          offersPush: z
            .strictObject({
              granted: z.boolean(),
              since: z.iso.datetime({ offset: true, error: 'Bitte einen Zeitpunkt angeben.' }),
            })
            .nullable(),
          newsletter: z.boolean({ error: 'Der Stand des Newsletters fehlt.' }),
        }),
      )
      .max(500, { error: 'Bitte höchstens 500 Einträge melden.' }),
  }),
  output: z.object({ count: z.int(), unknown: z.array(z.string()) }),
  permission: 'sales.order.write',
  risk: 'write',
  scope: 'record',
  publicApi: true,
  assistant: 'never',
  limits: { maxItems: 500, itemCount: (input) => input.consents.length },
})

// ---- Oberfläche: Anliegen ---------------------------------------------------------------------------------------------------------------

const zCustomerBrief = z.object({ id: zId, number: z.string().nullable(), name: z.string() })

export const zCaseRow = z.object({
  id: zId,
  caseId: z.string(),
  origin: zOrigin,
  kind: zKind,
  kindLabel: z.string(),
  status: zStatus,
  statusLabel: z.string(),
  /** Es ist noch etwas zu tun – in Merxo (Entscheidung) oder in der App (Ausführung) */
  open: z.boolean(),
  /** Das Personal muss entscheiden */
  needsDecision: z.boolean(),
  title: z.string(),
  /** eine Zeile für die Liste: Ansprechperson und Ort bzw. Konto */
  summary: z.string().nullable(),
  customer: zCustomerBrief.nullable(),
  accountEmail: z.string().nullable(),
  createdAt: z.date(),
  decidedAt: z.date().nullable(),
  doneAt: z.date().nullable(),
  /** Löschantrag nach 30 Tagen bzw. Interessent nach Kundenanlage oder Frist geleert: Angaben sind weg */
  anonymized: z.boolean(),
  // ---- additiv (Telefonassistent 21.10.2026) ----
  /** Herkunft für Menschen: „Bestell-App“, Ordaneo, „Telefonassistent (Vocaris)“ (TA-R15) – optional, damit ältere Stände passen */
  originLabel: z.string().optional(),
  /** Interessent: Rückrufnummer nur aus der Anrufer-Kennung – „Nummer nicht bestätigt“ (TA-R20) */
  unconfirmedPhone: z.boolean().optional(),
})

export const zCaseDetail = zCaseRow.extend({
  payload: z.record(z.string(), z.unknown()),
  decision: z
    .object({
      // additiv (Anfragen-Zentrale): order · transfer · done – E-Mail weiterverarbeitet bzw. erledigt
      action: z.enum(['create', 'assign', 'reject', 'execute', 'contacted', 'discard', 'expire', 'order', 'transfer', 'event', 'done']),
      customerNumber: z.string().nullable(),
      customerId: zId.nullable(),
      reason: z.string().nullable(),
    })
    .nullable(),
  decidedByName: z.string().nullable(),
  createdByName: z.string().nullable(),
  result: z.record(z.string(), z.unknown()).nullable(),
  message: z.string().nullable(),
  taskId: zId.nullable(),
  /** Bei geleertem Löschantrag: Tag des Leerens; sonst Tag, an dem ein abgeschlossener Löschantrag geleert wird */
  clearOn: z.string().nullable(),
})

const zCandidate = z.object({
  id: zId,
  number: z.string(),
  name: z.string(),
  city: z.string().nullable(),
  email: z.string().nullable(),
  vatId: z.string().nullable(),
  matchedBy: z.array(z.enum(['vatId', 'email', 'name', 'phone'])),
})

export const caseList = defineContract({
  name: 'sales.case.list',
  title: 'Anliegen des Vertriebskanals anzeigen',
  description: 'Anträge des Vertriebskanals und Aufträge an die App: offene zuerst, dazu die abgeschlossenen.',
  llm: {
    description:
      `Liefert die Anliegen zwischen Bestell-App und ${brand.name}: Geschäftskunden-Anträge und Löschanträge der App (warten auf Entscheidung) sowie Aufträge ` +
      `aus ${brand.name} (Konto zuordnen, Widerruf, Abo pausieren/fortsetzen). \`view\`: open (noch etwas zu tun), closed (erledigt, fehlgeschlagen) oder all.`,
    keywords: ['Anliegen', 'Geschäftskunden-Antrag', 'Löschantrag', 'Bestell-App', 'Konto zuordnen'],
  },
  input: z.strictObject({
    view: z.enum(['open', 'closed', 'all']).default('open'),
    kind: zKind.optional(),
    q: zText(100).optional(),
  }),
  output: z.object({
    rows: z.array(zCaseRow),
    counts: z.object({ open: z.int(), needsDecision: z.int(), closed: z.int() }),
  }),
  permission: 'sales.case.read',
  risk: 'read',
  scope: 'any',
})

export const caseGet = defineContract({
  name: 'sales.case.get',
  title: 'Anliegen des Vertriebskanals anzeigen (Einzelnes)',
  description:
    'Ein Anliegen mit Angaben, Entscheidung und Ergebnis – bei einem Geschäftskunden-Antrag mit möglichen vorhandenen Kunden.',
  llm: {
    description:
      'Liefert ein Anliegen mit seinen Angaben (Firma, Ansprechperson, Anschrift …), der Entscheidung, dem Ergebnis der App und – bei einem Geschäftskunden-Antrag – vorhandenen Kunden mit gleicher USt-IdNr., E-Mail oder gleichem Namen.',
    keywords: ['Anliegen', 'Antrag', 'Bestell-App'],
  },
  input: z.strictObject({ id: zId }),
  output: z.object({ case: zCaseDetail, candidates: z.array(zCandidate) }),
  permission: 'sales.case.read',
  risk: 'read',
  scope: 'any',
})

export const caseDecide = defineContract({
  name: 'sales.case.decide',
  title: 'Anliegen des Vertriebskanals entscheiden',
  description:
    'Entscheidet einen Antrag der App: Geschäftskunden-Antrag → Kunde anlegen (`create`), bestehendem Kunden zuordnen (`assign`) oder ablehnen (`reject`); ' +
    'Löschantrag → Löschung ausführen (`execute`) oder ablehnen (`reject`). Die App holt die Entscheidung ab und führt sie aus.',
  llm: {
    description:
      'Entscheidet einen offenen Antrag der Bestell-App. `create` legt aus den Angaben des Antrags einen Geschäftskunden an (optional Preisgruppe, Zahlungsbedingung, Kreditlimit; ' +
      'braucht zusätzlich das Recht, Kunden anzulegen). `assign` ordnet einen vorhandenen Kunden zu (`customerNumber`). `reject` lehnt mit Grund ab. `execute` bestätigt einen ' +
      'Löschantrag: Die App löscht das Konto, Rechnungen bleiben. Nicht umkehrbar. Nur nach ausdrücklicher Freigabe.',
    keywords: ['Antrag entscheiden', 'Kunde anlegen', 'Konto löschen', 'ablehnen', 'zuordnen'],
  },
  input: z.strictObject({
    id: zId,
    action: z.enum(['create', 'assign', 'reject', 'execute']),
    /** assign: Nummer des vorhandenen Kunden */
    customerNumber: zText(40).optional(),
    /** create: Preisgruppe und Zahlungsbedingung des neuen Kunden (Standard des Betriebs, wenn leer) */
    priceGroupId: zId.nullish(),
    paymentTermId: zId.nullish(),
    creditLimitCents: z.int().min(0).max(100_000_000).nullish(),
    /** reject: Grund; die App nennt ihn der Kundin */
    reason: zText(300).optional(),
  }),
  output: z.object({ case: zCaseDetail, customer: zCustomerBrief.nullable(), message: z.string() }),
  permission: 'sales.case.write',
  risk: 'destructive',
  scope: 'tenant',
  assistant: 'never',
  effects: { reversible: false },
})

/**
 * Interessent aus dem Telefonassistenten entscheiden (docs/integration/telefonassistent.md §6) – eigener Vertrag statt `sales.case.decide`,
 * damit Notiz und Grund NICHT ins versiegelte Audit-Protokoll kommen (`auditInput: 'omit'`): Sie können Personen und Rufnummern nennen und
 * werden mit der Frist des Zugangs geleert (TA-D8, TA-R22). Die Entscheidungen über Anträge der Bestell-App bleiben bei `sales.case.decide`.
 */
export const caseDecideLead = defineContract({
  name: 'sales.case.decideLead',
  title: 'Interessenten aus dem Telefonassistenten entscheiden',
  description:
    'Interessent → Kunde anlegen (`create`, privat oder gewerblich), kontaktiert, kein Kunde (`contacted`, Notiz) oder verwerfen (`discard`, Grund). ' +
    'Notiz und Grund stehen nur am Anliegen und werden mit der Frist geleert.',
  llm: {
    description:
      'Entscheidet einen offenen Interessenten aus dem Telefonassistenten: `create` legt aus den Angaben einen Kunden an (`customerType` privat/gewerblich), ' +
      '`contacted` vermerkt einen Rückruf ohne Kundenanlage (`reason` = Notiz), `discard` verwirft (`reason` = Grund). Nicht umkehrbar.',
    keywords: ['Interessent', 'Telefonassistent', 'Kunde anlegen', 'kontaktiert', 'verwerfen'],
  },
  input: z.strictObject({
    id: zId,
    action: z.enum(['create', 'contacted', 'discard']),
    /** create: privat oder gewerblich anlegen – vorbelegt aus der Einstufung des Anrufs, Pflicht bei „unbekannt“ */
    customerType: z.enum(['private', 'business']).optional(),
    /** contacted: was beim Rückruf herauskam · discard: Grund (je mindestens 3 Zeichen) */
    reason: zText(300).optional(),
  }),
  output: z.object({ case: zCaseDetail, customer: zCustomerBrief.nullable(), message: z.string() }),
  permission: 'sales.case.write',
  risk: 'destructive',
  scope: 'tenant',
  assistant: 'never',
  effects: { reversible: false },
  auditInput: 'omit',
})

export const caseRequest = defineContract({
  name: 'sales.case.request',
  title: 'Auftrag an den Vertriebskanal erteilen',
  description:
    'Erteilt der App einen Auftrag: Konto zuordnen, Widerruf der Werbe-Mitteilungen eintragen, Abo pausieren oder fortsetzen.',
  llm: {
    description:
      `Legt ein Anliegen aus ${brand.name} an, das die Bestell-App abholt und ausführt: \`link_account\` (App-Konto mit \`email\` dem Kunden \`customerId\` zuordnen), ` +
      '`revoke_offers_push` (Widerruf der Werbe-Mitteilungen des Kunden eintragen – nur auf Wunsch der Person), `pause_subscription` / `resume_subscription` ' +
      '(`subscriptionId`). Läuft schon ein gleiches Anliegen, wird kein zweites angelegt.',
    keywords: ['Konto zuordnen', 'Widerruf', 'Abo pausieren', 'Abo fortsetzen', 'Bestell-App'],
  },
  input: z.strictObject({
    kind: z.enum(MERXO_CASE_KINDS),
    customerId: zId.optional(),
    /** link_account: E-Mail-Adresse des Kontos in der Bestell-App */
    email: z.email({ error: 'Bitte eine gültige E-Mail-Adresse eingeben.' }).max(200).optional(),
    subscriptionId: zText(80).optional(),
  }),
  output: z.object({ case: zCaseDetail, created: z.boolean() }),
  permission: 'sales.case.write',
  risk: 'write',
  scope: 'tenant',
  assistant: 'never',
  effects: { reversible: false },
})

export const caseCount = defineContract({
  name: 'sales.case.count',
  title: 'Offene Anträge des Vertriebskanals zählen',
  description: 'Anzahl der Anträge des Vertriebskanals, die auf eine Entscheidung warten.',
  llm: {
    description:
      'Zählt die Anträge der Bestell-App (Geschäftskunden-Anträge, Löschanträge), die noch auf eine Entscheidung warten.',
    keywords: ['Anliegen', 'offen', 'Bestell-App'],
  },
  input: z.strictObject({}),
  output: z.object({ count: z.int() }),
  permission: 'sales.case.read',
  risk: 'read',
  scope: 'any',
})

// ---- Oberfläche: Abos und Kasten am Kunden ------------------------------------------------------------------------------------------------

export const zSubscriptionRow = z.object({
  id: zId,
  subscriptionId: z.string(),
  name: z.string(),
  accountEmail: z.string(),
  customer: zCustomerBrief.nullable(),
  /** Kundennummer laut App, auch wenn Merxo den Kunden nicht kennt */
  customerNumber: z.string().nullable(),
  rhythm: z.string(),
  rhythmText: z.string(),
  weekday: z.int(),
  window: z.string().nullable(),
  lines: z.array(z.object({ sku: z.string(), name: z.string().nullable(), qty: z.int() })),
  active: z.boolean(),
  nextDate: z.string().nullable(),
  lastRun: z
    .object({
      date: z.string(),
      result: z.string(),
      at: z.string(),
      orderNumber: z.string().nullable(),
      message: z.string().nullable(),
    })
    .nullable(),
  /** Ein Auftrag „pausieren/fortsetzen“ ist unterwegs */
  pendingKind: z.enum(['pause_subscription', 'resume_subscription']).nullable(),
})

export const appSubscriptionList = defineContract({
  name: 'sales.appSubscription.list',
  title: 'Abos des Vertriebskanals anzeigen',
  description:
    'Lesestand der Abos, die die Bestell-App selbst ausführt: Kunde, Rhythmus, nächster Termin, letztes Ergebnis.',
  llm: {
    description:
      'Liefert die Abos der Bestell-App (Lesestand, von der App gemeldet): Kunde, Rhythmus und Wochentag, Positionen, aktiv oder pausiert, nächster Termin und ' +
      'Ergebnis des letzten Laufs. `view`: active, paused oder all. Pausieren und Fortsetzen erteilt sales.case.request.',
    keywords: ['Abo', 'Abonnement', 'Bestell-App', 'wiederkehrend'],
  },
  input: z.strictObject({
    view: z.enum(['all', 'active', 'paused', 'failed']).default('all'),
    q: zText(100).optional(),
  }),
  output: z.object({
    rows: z.array(zSubscriptionRow),
    counts: z.object({ all: z.int(), active: z.int(), paused: z.int(), failed: z.int() }),
    /** Zeitpunkt der letzten Meldung der App; null = noch nie */
    reportedAt: z.date().nullable(),
  }),
  permission: 'sales.case.read',
  risk: 'read',
  scope: 'any',
})

export const caseCustomer = defineContract({
  name: 'sales.case.customer',
  title: 'Konto des Vertriebskanals am Kunden anzeigen',
  description: 'Konto des Vertriebskanals, Einwilligungen, Abos und Anliegen eines Kunden.',
  llm: {
    description:
      `Liefert zu einem Kunden, was ${brand.name} über sein Konto in der Bestell-App weiß: ob es verknüpft ist (E-Mail des Kontos), den Stand der Einwilligungen ` +
      '(Werbe-Mitteilungen: eingewilligt seit … / widerrufen am …, Newsletter), seine Abos und die jüngsten Anliegen.',
    keywords: ['Bestell-App', 'Konto', 'Einwilligung', 'Werbe-Mitteilungen', 'Newsletter', 'Abo'],
  },
  input: z.strictObject({ customerId: zId }),
  output: z.object({
    customer: zCustomerBrief,
    linked: z.boolean(),
    accountEmails: z.array(z.string()),
    consent: z
      .object({
        offersPush: z.enum(['granted', 'revoked', 'never']),
        offersPushSince: z.date().nullable(),
        newsletter: z.boolean(),
        reportedAt: z.date(),
      })
      .nullable(),
    subscriptions: z.array(zSubscriptionRow),
    cases: z.array(zCaseRow),
  }),
  permission: 'sales.case.read',
  risk: 'read',
  scope: 'any',
})
